import type { IoTReading, NaveSensores } from './queries/iot';

export interface SensorEquipment {
  environment: string[];
  ammonia: boolean;
  water: boolean;
  silo: boolean;
}

type ConfiguredNave = { sensor_equipment?: SensorEquipment | null };

// Legacy responses have no station label. Assign it before any filtering.
function stationName(index: number): string {
  let label = '';
  for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26))
    label = String.fromCharCode(65 + ((value - 1) % 26)) + label;
  return label;
}

export function getSensorEquipment(
  nave?: ConfiguredNave | null,
  data?: NaveSensores | null,
): SensorEquipment {
  const declared = nave?.sensor_equipment ?? data?.equipment;
  return declared
    ? { ...declared, environment: [...declared.environment] }
    : {
        environment: (data?.environment ?? []).map(
          (device, index) => device.station ?? stationName(index),
        ),
        ammonia: data?.ammonia != null,
        water: data?.water != null,
        silo: data?.silo != null,
      };
}

function missingReading(unit: string): IoTReading {
  return {
    value: null,
    unit,
    state: 'no_data',
    alert_type: null,
    received_at: null,
    measured_at: null,
    sensor_id: null,
  };
}

/** Presentation only: keep the raw cached inventory for later reactivation. */
export function applySensorEquipment(
  data: NaveSensores | undefined | null,
  equipment: SensorEquipment,
  naveId?: number,
): NaveSensores {
  const inventory = new Map(
    (data?.environment ?? []).map((device, index) => [
      device.station ?? stationName(index),
      device,
    ]),
  );
  return {
    ...data,
    nave_id: data?.nave_id ?? naveId ?? 0,
    equipment: { ...equipment, environment: [...equipment.environment] },
    environment: [...equipment.environment]
      .sort((a, b) => a.length - b.length || a.localeCompare(b))
      .map((station) => {
        const device = inventory.get(station);
        return {
          ...device,
          station,
          device_id: device?.device_id ?? null,
          temperature: device?.temperature ? { ...device.temperature } : missingReading('celsius'),
          humidity: device?.humidity ? { ...device.humidity } : missingReading('percent'),
        };
      }),
    ammonia: equipment.ammonia
      ? data?.ammonia
        ? { ...data.ammonia }
        : missingReading('ppm')
      : null,
    water: equipment.water ? (data?.water ? { ...data.water } : missingReading('liter')) : null,
    silo: equipment.silo ? (data?.silo ? { ...data.silo } : missingReading('kilogram')) : null,
  };
}
